// @flow strict

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline';

import classNames from 'classnames';
import useRouterPath from '@appmaker/core/hooks/useRouterPath';
import { isAdmin as checkIsAdmin } from '@appmaker/core/store/user';
import appmaker from '@appmaker/core/index';
import { getProjectId } from '@appmaker/core/store/project';
import { UserSquare } from 'iconsax-reactjs';

function CollapsibleGroup({
	group,
	pathname,
	getRoutePath,
	small = false,
	openGroup,
	setOpenGroup,
}) {
	const isOpen = openGroup === group.name;

	// Check if any item in this group is active
	const hasActiveItem = group.items.some((item) => {
		const itemPath = getRoutePath(item.routeName);
		let isItemActive =
			pathname === itemPath || pathname.startsWith(itemPath + '/');

		// For items with MenuItemComponent (like SubMenuItem), also check if pathname matches the route
		if (item.MenuItemComponent && !isItemActive) {
			isItemActive = pathname.match(itemPath);
		}

		return isItemActive;
	});

	const toggleOpen = () => {
		if (isOpen) {
			setOpenGroup(null); // Close if already open
		} else {
			setOpenGroup(group.name); // Open this group and close others
		}
	};

	return (
		<div className="space-y-1">
			<button
				onClick={toggleOpen}
				className={classNames(
					hasActiveItem
						? 'bg-purple-100 text-purple-600 border-l-4 border-purple-600 font-medium'
						: 'text-gray-900 hover:bg-gray-50 font-regular border-l-4 border-transparent',
					'group flex items-center w-full px-2 py-2 text-sm',
					small && 'justify-center'
				)}
			>
				<group.icon
					className={classNames(
						hasActiveItem ? 'text-purple-600' : 'text-gray-900',
						'flex-shrink-0 h-6 w-6'
					)}
					aria-hidden="true"
				/>
				{!small && (
					<>
						<span className="line-clamp-1 ml-3 flex-1 text-left">
							{group.name}
						</span>
						{isOpen ? (
							<ChevronDownIcon className="h-4 w-4 text-gray-400" />
						) : (
							<ChevronRightIcon className="h-4 w-4 text-gray-400" />
						)}
					</>
				)}
			</button>
			{isOpen && !small && (
				<div className="ml-6 space-y-1">
					{group.items.map((item, id) => {
						if (item.adminOnly && !checkIsAdmin()) {
							return null;
						}
						return (
							<MenuItemRender
								key={`${item.name}-${id}`}
								item={item}
								pathname={pathname}
								getRoutePath={getRoutePath}
								isInnerItem={true}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

function MenuLink({ item, pathname, getRoutePath, isInnerItem = false }) {
	const projectId = getProjectId();
	const itemPath = getRoutePath(item.routeName);
	let isActive = pathname === itemPath || pathname.startsWith(itemPath + '/');

	// For items with MenuItemComponent (like SubMenuItem), also check if pathname matches the route
	if (item.MenuItemComponent && !isActive) {
		isActive = pathname.match(itemPath);
	}

	// Apply different styling based on whether it's an inner item or main item
	const child = (
		<item.icon
			className={classNames(
				isInnerItem
					? isActive
						? 'text-gray-900 h-5 w-5'
						: 'text-gray-400 h-5 w-5'
					: isActive
					? 'text-purple-600 h-6 w-6'
					: 'text-gray-900 h-6 w-6',
				'flex-shrink-0'
			)}
			aria-hidden="true"
		/>
	);
	const className = classNames(
		isInnerItem
			? isActive
				? 'text-gray-900 font-medium'
				: 'text-gray-400 hover:bg-gray-50 font-regular'
			: isActive
			? 'bg-purple-100 text-purple-600 border-l-4 border-purple-600 font-medium'
			: 'text-gray-900 hover:bg-gray-50 font-regular border-l-4 border-transparent',
		'group flex items-center px-2 py-2 text-sm'
	);
	return item.href ? (
		<a href={item.href} className={className}>
			{child}
			<span className="line-clamp-1 ml-3">{item.name}</span>
			{/* Hide when small = "true" */}
		</a>
	) : (
		<Link
			to={getRoutePath(item.routeName)}
			className={className}
			onClick={() => {
				if (item && item.addTracker) {
					appmaker.doAction('appmaker-analytics-event', {
						event: item.addTracker.eventName,
						params: {
							project_id: projectId,
						},
					});
				}
			}}
		>
			{child}
			<span className="line-clamp-1 ml-3">{item.name}</span>
			{/* Hide when small = "true" */}
		</Link>
	);
}
function MenuItemRender({
	item,
	pathname,
	getRoutePath,
	small = false,
	openGroup,
	setOpenGroup,
	isInnerItem = false,
}) {
	if (item.isGroup) {
		return (
			<CollapsibleGroup
				group={item}
				pathname={pathname}
				getRoutePath={getRoutePath}
				small={small}
				openGroup={openGroup}
				setOpenGroup={setOpenGroup}
			/>
		);
	}

	return item.MenuItemComponent ? (
		<item.MenuItemComponent
			item={item}
			pathname={pathname}
			itemRoute={item.routeName}
			routeName={getRoutePath(item.routeName)}
		/>
	) : (
		<MenuLink
			item={item}
			pathname={pathname}
			getRoutePath={getRoutePath}
			isInnerItem={isInnerItem}
		/>
	);
}
function SidebarNavigation({ navigation, basePath, small = false }) {
	const routerPath = useRouterPath();
	let { pathname } = useLocation();
	const [openGroup, setOpenGroup] = React.useState(null);

	const getRoutePath = React.useCallback(
		(routeName) => routerPath(`${basePath ? basePath : ''}${routeName}`),
		[routerPath, basePath]
	);

	// Note: Removed auto-open logic to allow manual group control
	// Groups will show active styling when they contain active items, but won't auto-open

	return (
		<nav
			className={classNames(
				'mt-2 flex-1 bg-white space-y-1',
				small && 'flex flex-col items-center'
			)}
		>
			{navigation.map((item, id) => {
				if (item.adminOnly && !checkIsAdmin()) {
					return null;
				}
				return (
					<MenuItemRender
						key={`${item.name}-${id}`}
						item={item}
						pathname={pathname}
						getRoutePath={getRoutePath}
						small={small}
						openGroup={openGroup}
						setOpenGroup={setOpenGroup}
					/>
				);
			})}
		</nav>
	);
}

function MyAccountSection({ pathname, getRoutePath }) {
	return (
		<MenuLink
			item={{
				name: 'My Account',
				routeName: '/subscription',
				icon: UserSquare,
			}}
			pathname={pathname}
			getRoutePath={getRoutePath}
		/>
	);
}

export const SideNavFooter = ({ small = false, pathname, getRoutePath }) => (
	<MyAccountSection pathname={pathname} getRoutePath={getRoutePath} />
);

export default SidebarNavigation;
